import { useState } from "react";
import { Box, Checkbox, TableCell } from "@mui/material";
import { useParams } from "react-router";
import {
    Color,
    DotLegalEmptyState,
    DotLegalHeader,
    DotLegalInformationTooltip,
    DotLegalLink,
    DotLegalPaper,
    DotLegalTable,
    DotLegalTooltip,
    ITableHeader,
    ServerSideConfig,
} from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import TableRow from "@common/components/tableRow/TableRow";
import ListPageToolbar from "@common/components/listPageToolbar/ListPageToolbar";
import FilterBar from "@common/components/filterBar/FilterBar";
import SavedViewsMenu from "@common/components/savedViews/SavedViewsMenu";
import ExcelExportButton from "@common/components/excelExportButton/ExcelExportButton";
import TableDisplayFields from "@common/components/tableDisplayFields/TableDisplayFields";
import EntityTypeIconWithName from "@common/components/entityTypeIcon/EntityTypeIconWithName";
import { RiskFieldGroupingOrder } from "@common/components/filterPopover/FilterPopover.types";
import { FilterPage } from "@common/pageView/PageView.types";
import { SortDirection } from "@common/api/filterTypes";
import { htmlToPlainText } from "@common/stringOperations";
import { useRiskMitigationOverview } from "./RiskMitigationOverview.hooks";
import { useRiskMitigationOverviewStyles } from "./RiskMitigationOverview.styles";
import { RiskMitigationColumn, RiskMitigationItem, RiskMitigationPlanStatus } from "./RiskMitigationOverview.types";
import { entityTypeLabel } from "../riskOperationalOverview/RiskOperationalOverview.types";
import { formatExposureCost, formatExposureValue } from "../riskExposure/formatExposureValue";
import { RiskExposureMode } from "../riskExposure/RiskExposure.types";
import ScheduleMitigationDialog from "./ScheduleMitigationDialog";
import AcceptRiskDialog from "./AcceptRiskDialog";
import AvoidRiskDialog from "./AvoidRiskDialog";
import CancelPlanDialog from "./CancelPlanDialog";
import CompleteMitigationPlanDialog from "./CompleteMitigationPlanDialog";
import MitigationStatusMenu from "./MitigationStatusMenu";
import PromoteMitigationDialog from "./PromoteMitigationDialog";
import { BulkPromoteSelection } from "./useBulkPromoteSelection";
import { isPlanOverdue } from "./RiskMitigationOverview.utils";
import DotLegalWarningTriangle from "@common/components/dotLegalWarningTriangle/DotLegalWarningTriangle";
import MitigationPlanDialog from "../riskAssessmentsOverview/treatmentDialogs/MitigationPlanDialog";
import { getRiskSettingsIcon } from "../../riskSettings/common/RiskSettings.utils";
import { colorBackgroundHex, mitigationStatusColor } from "../riskMitigationStatus";
import RiskLevelChip from "../RiskLevelChip";
import RiskMitigationStatusChip from "../RiskMitigationStatusChip";
import RiskCountCellLink from "../riskControls/RiskCountCellLink";
import LinkedControlStatus from "../riskControls/LinkedControlStatus";
import LinkedControlsDialog from "../riskControls/LinkedControlsDialog";
import { usePlanContext } from "../../auth/planProvider/PlanProvider";
import TablePagePaper from "@common/components/tablePagePaper/TablePagePaper";

interface RiskMitigationOverviewProps {
    bulkPromote: BulkPromoteSelection;
}

function RiskMitigationOverview({ bulkPromote }: RiskMitigationOverviewProps) {
    const styles = useRiskMitigationOverviewStyles();
    const { translateString, translateDate, translateNumber } = useTranslation();
    const { addonsFeatures } = usePlanContext();
    const { riskAreaId } = useParams<{ riskAreaId: string }>();

    const {
        result,
        kpis,
        isLoading,
        isPlaceholderData,
        hasPlans,
        pageNumber,
        pageSize,
        sortByColumn,
        sortDirection,
        onSortChange,
        onPageChange,
        filterOptions,
        onFilterChange,
        clearFilterData,
        isKpiActive,
        onKpiFilter,
        searchText,
        onSearchTextChange,
        visibleColumns,
        renderedColumns,
        tableFields,
        hasDisplayChanged,
        hasFilterChanged,
        isSavedView,
        savedView,
        currentViewId,
        filterData,
        onColumnsSave,
        onViewSaved,
        onViewDeleted,
        onChangeView,
        onDownloadExcelReportClick,
        getRiskChipStyle,
        getItemUrl,
        getStatusMenuGroups,
        onStatusSelect,
        isChangingStatus,
        openMitigationPlan,
        selectedScenario,
        selectedPlanId,
        onMitigationPlanSaved,
        planDialog,
        acceptDialog,
        avoidDialog,
        cancelDialog,
        completeDialog,
        closeDialogs,
        submitPlan,
        submitAcceptRisk,
        submitAvoidRisk,
        submitCancelPlan,
        submitCompletePlan,
        canPromote,
        closePromote,
        submitPromote,
        promoteDialog,
        isPromoting,
        responsibleOptions,
        isLoadingResponsibles,
        pinnedFilterIds,
        setPinnedFilterIds,
    } = useRiskMitigationOverview(riskAreaId);

    const { isSelectionMode, isSelected, toggleItem, onSelectAllChanged, getHeaderCheckBoxState, selectedItems } = bulkPromote;
    const pageItems = result?.items ?? [];

    // One confirm dialog serves both entry points: the per-row kebab (promoteDialog holds its single item) and the
    // bulk button in the tab row (bulkPromote.isConfirmOpen, acting on the current selection).
    const promoteItems = promoteDialog?.items ?? (bulkPromote.isConfirmOpen ? selectedItems : null);
    const closePromoteDialog = () => {
        closePromote();
        bulkPromote.closeConfirm();
    };

    const [statusMenu, setStatusMenu] = useState<{ anchorEl: HTMLElement; item: RiskMitigationItem } | null>(null);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);

    // The read-only controls dialog opened from the count cell — the plan's own controls (round scope).
    const [controlsDialogItem, setControlsDialogItem] = useState<RiskMitigationItem | null>(null);

    const columnHeader: Record<RiskMitigationColumn, string> = {
        [RiskMitigationColumn.Name]: translateString("name"),
        [RiskMitigationColumn.Action]: translateString("riskMitigationColumnAction"),
        [RiskMitigationColumn.Type]: translateString("type"),
        [RiskMitigationColumn.Entity]: translateString("entity"),
        [RiskMitigationColumn.Scenario]: translateString("scenario"),
        [RiskMitigationColumn.CurrentRisk]: translateString("risk"),
        [RiskMitigationColumn.ExpectedRisk]: translateString("afterMitigation"),
        [RiskMitigationColumn.Responsible]: translateString("responsible"),
        [RiskMitigationColumn.Deadline]: translateString("deadline"),
        [RiskMitigationColumn.CreatedDate]: translateString("created"),
        [RiskMitigationColumn.Status]: translateString("planStatus"),
        [RiskMitigationColumn.EstimatedCost]: translateString("estimatedCost"),
        [RiskMitigationColumn.EstimatedLoss]: translateString("riskExposure"),
        [RiskMitigationColumn.ExposureAfterMitigation]: translateString("exposureAfterMitigationLabel"),
        [RiskMitigationColumn.ControlCount]: translateString("controlCount"),
        [RiskMitigationColumn.ControlStatus]: translateString("controlStatus"),
    };

    const headers: Array<ITableHeader<RiskMitigationItem>> = renderedColumns.map((column) => ({
        text: columnHeader[column],
        property: column,
        // The status roll-up is task-derived and not sortable, mirroring the assessments overview; the subject
        // type is a rendered label rather than a stored column, so it has nothing to sort on either.
        notSortable: column === RiskMitigationColumn.ControlStatus || column === RiskMitigationColumn.Type,
    }));

    const formatModeExposure = (value: number | null) =>
        formatExposureValue(
            value,
            result?.exposureMode ?? RiskExposureMode.None,
            result?.exposureCurrencyCode ?? null,
            result?.exposureScaleUpperBound ?? null,
            translateNumber
        );
    const formatCost = (value: number | null) => formatExposureCost(value, result?.exposureCurrencyCode ?? null, translateNumber);

    const totalCount = result?.totalCount ?? 0;

    const serverSideConfig: ServerSideConfig = {
        order: sortDirection === SortDirection.DESC ? "desc" : "asc",
        orderBy: sortByColumn,
        onSortChange: (columnId, direction) => {
            onSortChange(columnId as RiskMitigationColumn, direction === "asc" ? SortDirection.ASC : SortDirection.DESC);
        },
        page: pageNumber,
        from: totalCount > 0 ? (pageNumber - 1) * pageSize + 1 : 0,
        to: Math.min(pageNumber * pageSize, totalCount),
        totalPages: result?.totalPages ?? 1,
        totalCount: totalCount,
        onPageChange: onPageChange,
    };

    const renderRiskCell = (key: string, level: RiskMitigationItem["currentRisk"], drifted?: boolean) => {
        const style = level ? getRiskChipStyle(level.level) : null;
        return (
            <TableCell key={key}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {level && style && <RiskLevelChip level={level.level} label={level.label} color={style.color} icon={style.icon} />}
                    {drifted && <DotLegalWarningTriangle warningText={translateString("riskTreatmentDriftPlan")} />}
                </Box>
            </TableCell>
        );
    };

    // Completing the linked board task now auto-completes the plan, so the nudge fires on a Completed (not yet
    // applied) plan whose task was reported done, prompting the owner to apply the mitigation.
    const showApplyMitigationNudge = (item: RiskMitigationItem) => item.linkedTaskCompleted && item.status === RiskMitigationPlanStatus.Completed;

    const renderStatusCell = (item: RiskMitigationItem) => {
        // Cancelled/Promoted have no actionable menu items, so their chip is a plain read-only badge. Completed
        // still opens the menu (its only enabled action is Promote), so it must stay clickable.
        const hasNoStatusMenu = item.status === RiskMitigationPlanStatus.Cancelled || item.status === RiskMitigationPlanStatus.Promoted;
        const chip = hasNoStatusMenu ? (
            <RiskMitigationStatusChip mitigationPlanStatus={item.status} />
        ) : (
            <RiskMitigationStatusChip
                mitigationPlanStatus={item.status}
                showCaret
                onClick={(event) => {
                    event.stopPropagation();
                    setStatusMenu({ anchorEl: event.currentTarget, item });
                    setStatusMenuOpen(true);
                }}
            />
        );

        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {chip}
                {showApplyMitigationNudge(item) && <DotLegalInformationTooltip text={translateString("riskMitigationLinkedTaskDone")} />}
            </Box>
        );
    };

    const onStatusMenuSelect = (id: string) => {
        if (!statusMenu) return;
        onStatusSelect(statusMenu.item, id);
        setStatusMenuOpen(false);
    };

    const renderCell = (column: RiskMitigationColumn, item: RiskMitigationItem) => {
        switch (column) {
            case RiskMitigationColumn.Name:
                return (
                    <TableCell key={column}>
                        <DotLegalTooltip text={item.name}>
                            <Box sx={styles.nameCell}>{item.name}</Box>
                        </DotLegalTooltip>
                    </TableCell>
                );
            case RiskMitigationColumn.Action: {
                const actionText = htmlToPlainText(item.description);
                const displayText = actionText || translateString("riskMitigationNoDescription");
                return (
                    <TableCell key={column}>
                        <DotLegalTooltip text={displayText}>
                            <Box sx={{ ...styles.ellipsis, ...(actionText ? styles.bold : styles.italic) }}>{displayText}</Box>
                        </DotLegalTooltip>
                    </TableCell>
                );
            }
            case RiskMitigationColumn.Type:
                return (
                    <TableCell key={column}>
                        {/* What the plan's risk is about: one of the assessable entity types, or the
                            organisation as a whole, whose plans carry no entity at all. */}
                        {item.entityType !== null ? (
                            <EntityTypeIconWithName type={item.entityType} name={entityTypeLabel(item.entityType, translateString)} />
                        ) : (
                            translateString("organization")
                        )}
                    </TableCell>
                );
            case RiskMitigationColumn.Entity:
                return (
                    <TableCell key={column}>
                        {/* Just the name: the Type column beside it carries the type icon, so repeating it here
                            would say the same thing twice. An organisation-level plan has no entity to name, so
                            the cell stays empty and the scenario column carries what the plan is about. */}
                        {item.entityName !== null && (
                            <DotLegalLink to={getItemUrl(item)} linkColor="primary" bold stopPropagation>
                                {item.entityName}
                            </DotLegalLink>
                        )}
                    </TableCell>
                );
            case RiskMitigationColumn.Scenario:
                return (
                    <TableCell key={column}>
                        <DotLegalTooltip text={item.scenarioName}>
                            <Box sx={styles.ellipsis}>{item.scenarioName}</Box>
                        </DotLegalTooltip>
                    </TableCell>
                );
            case RiskMitigationColumn.CurrentRisk:
                return renderRiskCell(column, item.currentRisk);
            case RiskMitigationColumn.ExpectedRisk: {
                // A promoted plan has realized its after-mitigation risk as the current risk, so the expected
                // value now just duplicates current (and would be a stale target if the plan had drifted) — hide it.
                const promoted = item.status === RiskMitigationPlanStatus.Promoted;
                return renderRiskCell(column, promoted ? null : item.expectedRisk, promoted ? false : item.isDrifted);
            }
            case RiskMitigationColumn.Responsible:
                return <TableCell key={column}>{item.responsibleName ?? ""}</TableCell>;
            case RiskMitigationColumn.Deadline:
                return (
                    <TableCell key={column}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {item.deadline ? translateDate(new Date(item.deadline)) : ""}
                            {isPlanOverdue(item.deadline, item.status) && <DotLegalWarningTriangle warningText={translateString("deadlineOverdue")} />}
                        </Box>
                    </TableCell>
                );
            case RiskMitigationColumn.CreatedDate:
                return <TableCell key={column}>{translateDate(new Date(item.createdDate))}</TableCell>;
            case RiskMitigationColumn.Status:
                return <TableCell key={column}>{renderStatusCell(item)}</TableCell>;
            case RiskMitigationColumn.EstimatedCost:
                return (
                    <TableCell key={column} align="right">
                        {formatCost(item.estimatedCost)}
                    </TableCell>
                );
            case RiskMitigationColumn.EstimatedLoss:
                return (
                    <TableCell key={column} align="right">
                        {formatModeExposure(item.grossExposure)}
                    </TableCell>
                );
            case RiskMitigationColumn.ExposureAfterMitigation:
                return (
                    <TableCell key={column} align="right">
                        {formatModeExposure(item.expectedExposure)}
                    </TableCell>
                );
            case RiskMitigationColumn.ControlCount:
                return (
                    <TableCell key={column} align="right">
                        <RiskCountCellLink count={item.linkedControlIds.length} onClick={() => setControlsDialogItem(item)} />
                    </TableCell>
                );
            case RiskMitigationColumn.ControlStatus:
                return (
                    <TableCell key={column}>
                        <LinkedControlStatus status={item.linkedControlStatus} />
                    </TableCell>
                );
            default:
                return null;
        }
    };

    const renderKpiCard = (value: number, label: string, subLabel: string, accentColor: Color, statuses: Array<number>) => (
        <DotLegalPaper
            clickableRows
            onClick={() => onKpiFilter(statuses)}
            sx={{
                ...styles.kpiCard,
                ...(isKpiActive(statuses) ? styles.kpiCardActive : {}),

                position: "relative",
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    borderRadius: "inherit",
                    backgroundImage: `linear-gradient(to right, ${colorBackgroundHex[accentColor]}, color-mix(in srgb, ${colorBackgroundHex[accentColor]} 30%, white))`,
                    filter: "saturate(1.8)",
                    zIndex: 0,
                },
                "& > *": { position: "relative", zIndex: 1 },
            }}
        >
            <Box sx={{ "& .MuiTypography-root": { fontSize: "2rem" } }}>
                <DotLegalHeader headerStyle={"xxxl"} marginBottom={0} fontWeight={300}>
                    {value}
                </DotLegalHeader>
            </Box>
            <DotLegalHeader headerStyle={"small"} marginBottom={0} fontWeight={700}>
                {label}
            </DotLegalHeader>
            <DotLegalHeader headerStyle={"extraSmall"} marginBottom={0} fontWeight={400}>
                {subLabel}
            </DotLegalHeader>
        </DotLegalPaper>
    );

    return (
        <Box sx={styles.container}>
            <Box sx={styles.kpiRow}>
                {renderKpiCard(
                    kpis?.proposed ?? 0,
                    translateString("riskMitigationStatusProposed"),
                    translateString("riskMitigationKpiProposedSub"),
                    mitigationStatusColor[RiskMitigationPlanStatus.Proposed],
                    [RiskMitigationPlanStatus.Proposed]
                )}
                {renderKpiCard(
                    kpis?.approved ?? 0,
                    translateString("riskMitigationStatusApproved"),
                    translateString("riskMitigationKpiApprovedSub"),
                    mitigationStatusColor[RiskMitigationPlanStatus.Approved],
                    [RiskMitigationPlanStatus.Approved]
                )}
                {renderKpiCard(
                    kpis?.planned ?? 0,
                    translateString("riskMitigationStatusPlanned"),
                    "",
                    mitigationStatusColor[RiskMitigationPlanStatus.Planned],
                    [RiskMitigationPlanStatus.Planned]
                )}
                {renderKpiCard(
                    kpis?.inProgress ?? 0,
                    translateString("riskMitigationStatusInProgress"),
                    "",
                    mitigationStatusColor[RiskMitigationPlanStatus.InProgress],
                    [RiskMitigationPlanStatus.InProgress]
                )}
                {renderKpiCard(
                    kpis?.completed ?? 0,
                    translateString("riskMitigationStatusCompleted"),
                    "",
                    mitigationStatusColor[RiskMitigationPlanStatus.Completed],
                    [RiskMitigationPlanStatus.Completed]
                )}
                {renderKpiCard(
                    kpis?.cancelled ?? 0,
                    translateString("riskMitigationStatusCancelled"),
                    "",
                    mitigationStatusColor[RiskMitigationPlanStatus.Cancelled],
                    [RiskMitigationPlanStatus.Cancelled]
                )}
                {renderKpiCard(
                    kpis?.promoted ?? 0,
                    translateString("riskMitigationStatusPromoted"),
                    "",
                    mitigationStatusColor[RiskMitigationPlanStatus.Promoted],
                    [RiskMitigationPlanStatus.Promoted]
                )}
            </Box>

            <TablePagePaper>
                {hasPlans ? (
                    <>
                        <ListPageToolbar
                            savedViewsMenu={
                                <SavedViewsMenu
                                    currentViewId={currentViewId}
                                    parentIsLoading={isLoading}
                                    currentFilterData={filterData}
                                    currentDisplayData={visibleColumns}
                                    onViewChange={onChangeView}
                                    onViewSaved={onViewSaved}
                                    onViewDeleted={onViewDeleted}
                                    filterPage={FilterPage.RiskMitigationOverview}
                                    scopeId={riskAreaId}
                                    hasFilterOrDisplayDataChanged={hasFilterChanged || hasDisplayChanged}
                                />
                            }
                            leftContent={
                                <FilterBar
                                    isLoading={isLoading}
                                    fetchFilterData
                                    filterData={filterOptions}
                                    onMenuClose={onFilterChange}
                                    onRemove={onFilterChange}
                                    pinnedFilterIds={pinnedFilterIds}
                                    onPinnedFilterIdsChange={setPinnedFilterIds}
                                    onClearFilter={hasFilterChanged ? () => clearFilterData() : undefined}
                                    clearFilterText={hasFilterChanged ? (isSavedView ? "savedView" : "defaultView") : undefined}
                                    searchValue={searchText}
                                    onSearchChange={onSearchTextChange}
                                    searchLabel={translateString("search")}
                                />
                            }
                            rightContent={
                                <>
                                    {savedView && (
                                        <TableDisplayFields
                                            selectedFields={visibleColumns}
                                            onSave={onColumnsSave}
                                            options={tableFields}
                                            hasDisplayChanged={hasDisplayChanged}
                                            isCustomView={isSavedView}
                                            savedViewFields={savedView.displayData}
                                            groupingOrder={RiskFieldGroupingOrder}
                                        />
                                    )}
                                    {savedView && (
                                        <ExcelExportButton
                                            onClick={onDownloadExcelReportClick}
                                            isDisabled={isLoading}
                                            isPlanLocked={!addonsFeatures.customLists}
                                            planLockedText={translateString("upgradeToCustomList")}
                                        />
                                    )}
                                </>
                            }
                        />

                        <DotLegalTable
                            internalScroll
                            headers={headers}
                            defaultOrderBy={RiskMitigationColumn.CreatedDate as keyof RiskMitigationItem}
                            defaultOrder={"desc"}
                            data={pageItems}
                            isLoading={isLoading || isPlaceholderData}
                            disableFilterAndSortAndSlicing
                            serverSide={serverSideConfig}
                            rowsPerPage={pageSize}
                            paginationLabelOf={translateString("labelOf")}
                            emptyText={translateString("noRiskMitigationPlansMatchesQuery")}
                            clickableRows
                            onSelectAllChanged={isSelectionMode ? () => onSelectAllChanged(pageItems) : undefined}
                            headerCheckBoxState={isSelectionMode ? getHeaderCheckBoxState(pageItems) : undefined}
                            renderRow={(item) => {
                                // In selection mode only completed plans can be promoted, so an ineligible row is not a
                                // selection target. Outside selection mode every row opens the plan (read-only for a
                                // terminal plan — the dialog itself blocks editing).
                                const onRowClick = isSelectionMode ? (canPromote(item) ? () => toggleItem(item) : undefined) : () => openMitigationPlan(item);
                                return (
                                    <TableRow key={item.planId} onClick={onRowClick} sx={{ cursor: onRowClick ? "pointer" : "default" }}>
                                        {isSelectionMode && (
                                            <TableCell padding="checkbox">
                                                {canPromote(item) ? (
                                                    <Checkbox checked={isSelected(item)} readOnly tabIndex={-1} />
                                                ) : (
                                                    // A disabled checkbox doesn't emit hover events, so the span makes the tooltip reachable.
                                                    // The theme forces every checkbox icon blue, so grey the icon explicitly to read as disabled.
                                                    <DotLegalTooltip text={translateString("riskMitigationPromoteDisabled")}>
                                                        <span>
                                                            <Checkbox
                                                                checked={false}
                                                                disabled
                                                                readOnly
                                                                tabIndex={-1}
                                                                sx={{ "& .MuiSvgIcon-root": { fill: (theme) => theme.palette.grey[400] } }}
                                                            />
                                                        </span>
                                                    </DotLegalTooltip>
                                                )}
                                            </TableCell>
                                        )}
                                        {renderedColumns.map((column) => renderCell(column, item))}
                                    </TableRow>
                                );
                            }}
                        />
                    </>
                ) : (
                    <DotLegalEmptyState icon={getRiskSettingsIcon()} text={translateString("riskMitigationEmptyState")} />
                )}
            </TablePagePaper>

            <MitigationStatusMenu
                anchorEl={statusMenu?.anchorEl ?? null}
                open={statusMenuOpen}
                groups={statusMenu ? getStatusMenuGroups(statusMenu.item) : []}
                onSelect={onStatusMenuSelect}
                onClose={() => setStatusMenuOpen(false)}
                onExited={() => setStatusMenu(null)}
            />

            {planDialog && (
                <ScheduleMitigationDialog
                    initialResponsibleId={planDialog.responsibleId}
                    initialDeadline={planDialog.deadline}
                    responsibleOptions={responsibleOptions}
                    isLoadingResponsibles={isLoadingResponsibles}
                    inProgress={isChangingStatus}
                    canTrackAsTask={planDialog.canTrackAsTask}
                    onClose={closeDialogs}
                    onSubmit={submitPlan}
                />
            )}

            {acceptDialog && <AcceptRiskDialog inProgress={isChangingStatus} onClose={closeDialogs} onSubmit={submitAcceptRisk} />}

            {avoidDialog && <AvoidRiskDialog inProgress={isChangingStatus} onClose={closeDialogs} onSubmit={submitAvoidRisk} />}

            {cancelDialog && <CancelPlanDialog inProgress={isChangingStatus} onClose={closeDialogs} onSubmit={submitCancelPlan} />}

            {controlsDialogItem && <LinkedControlsDialog linkedControlIds={controlsDialogItem.linkedControlIds} onClose={() => setControlsDialogItem(null)} />}

            {completeDialog && (
                <CompleteMitigationPlanDialog
                    frameworkControlIds={completeDialog.frameworkControlIds}
                    inProgress={isChangingStatus}
                    onClose={closeDialogs}
                    onSubmit={submitCompletePlan}
                />
            )}

            {promoteItems && (
                <PromoteMitigationDialog
                    count={promoteItems.length}
                    hasDriftedPlan={promoteItems.some((item) => item.isDrifted)}
                    inProgress={isPromoting}
                    onClose={closePromoteDialog}
                    onSubmit={() =>
                        submitPromote(
                            promoteItems.map((item) => item.riskAssessmentId),
                            () => {
                                bulkPromote.closeConfirm();
                                bulkPromote.exitSelectionMode();
                            }
                        )
                    }
                />
            )}

            {selectedScenario && (
                <MitigationPlanDialog scenario={selectedScenario} planId={selectedPlanId} onClose={closeDialogs} onSaved={onMitigationPlanSaved} />
            )}
        </Box>
    );
}

export default RiskMitigationOverview;
